import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, Maximize2Icon, PenBoxIcon, XIcon } from "lucide-react";

import ChatContentSubtitle from "./ChatContentSubtitle";
import type { ChatController } from "~/hooks/useChatController";
import useGlobalStore from "~/hooks/useGlobalStore";

interface ChatSidebarHeaderProps {
	controller: ChatController;
}

const ChatSidebarHeader = ({ controller }: ChatSidebarHeaderProps) => {
	const reduceMotion = useReducedMotion();
	const { expandChat } = useGlobalStore();
	const { isSignedIn, thread, view, setView, handleNewChat, handleClose } =
		controller;

	return (
		<div className="flex shrink-0 items-center justify-between border-slate-100 border-b px-4 py-3 dark:border-slate-800">
			<div className="flex min-w-0 items-center gap-2">
				{view === "conversation" && (
					<motion.button
						type="button"
						onClick={() => setView("history")}
						aria-label="Back to chat history"
						whileTap={reduceMotion ? undefined : { scale: 0.9 }}
						className="-ml-1.5 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
					>
						<ArrowLeftIcon className="size-5" strokeWidth={2.25} />
					</motion.button>
				)}

				<div className="flex min-w-0 flex-col">
					<span className="line-clamp-1 min-w-0 text-left font-semibold text-sm">
						{view === "history"
							? "Chat history"
							: (thread?.title ?? "AI Tutor")}
					</span>
					{view === "conversation" && (
						<ChatContentSubtitle controller={controller} />
					)}
				</div>
			</div>

			<div className="flex items-center gap-1">
				{isSignedIn && (
					<motion.button
						type="button"
						onClick={handleNewChat}
						className="tablet:flex hidden rounded-full p-1.5 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
					>
						<PenBoxIcon className="size-4" />
					</motion.button>
				)}
				{isSignedIn && (
					<motion.button
						type="button"
						onClick={expandChat}
						aria-label="Expand"
						className="tablet:flex hidden rounded-full p-1.5 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
					>
						<Maximize2Icon className="size-4" />
					</motion.button>
				)}
				<motion.button
					type="button"
					onClick={handleClose}
					aria-label="Close"
					className="rounded-full p-1.5 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
				>
					<XIcon className="size-5" />
				</motion.button>
			</div>
		</div>
	);
};

export default ChatSidebarHeader;
